import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useQuery } from 'urql';
import { useFragment } from '@frontend/gql/social';
import {
  Period,
  SocialAdsAnalytics_QueryFragment,
  GetInsightsByClientIdQuery
} from '@frontend/gql/social/graphql';
import { insightsByClientId, socialAdsAnalyticsFragment } from '../graphql';

interface UseAdsQueryDataProps {
  clientId?: string;
  currentIntervalRange: {
    startDate: string;
    endDate: string;
    precision: Period;
  };
  previousIntervalRange: {
    startDate: string;
    endDate: string;
  };
  startDate: string;
  endDate: string;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignAndSetIds?: string[];
  adObjectiveIds?: string[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
}

// Type for ad account from the SocialAdsAnalytics fragment
type AdAccountFromFragment = NonNullable<NonNullable<SocialAdsAnalytics_QueryFragment['client']>['metaAdAccounts']>[number];

/**
 * Helper function to check if an ad account should be included based on filter criteria
 * Note: Since the SocialAdsAnalytics fragment doesn't include metaAdCampaigns,
 * we can only filter by adAccountIds for now. Campaign and objective filtering
 * will need to be implemented when the fragment includes campaign data.
 */
const shouldIncludeAdAccount = (
  adAccount: AdAccountFromFragment,
  adAccountIds?: string[],
  adCampaignAndSetIds?: string[],
  adObjectiveIds?: string[]
): boolean => {
  // Ensure adAccount is not null
  if (!adAccount) return false;

  // If adAccountIds filter is specified and this account is not in the list, exclude it
  if (adAccountIds && adAccountIds.length > 0 && !adAccountIds.includes(adAccount.id)) {
    return false;
  }

  // TODO: Campaign and objective filtering cannot be implemented here because
  // the SocialAdsAnalytics fragment doesn't include metaAdCampaigns data.
  // This filtering will need to be done server-side when the GraphQL schema supports it.
  if (adCampaignAndSetIds && adCampaignAndSetIds.length > 0) {
    // Campaign/AdSet filtering is not yet supported - requires server-side implementation
  }

  if (adObjectiveIds && adObjectiveIds.length > 0) {
    // Objective filtering is not yet supported - requires server-side implementation
  }

  return true;
};

/**
 * Client-side filtering function for metaAdAccounts
 * Filters the data that the GraphQL query doesn't support filtering for
 */
const applyClientSideFiltering = (
  data: GetInsightsByClientIdQuery,
  adAccountIds?: string[],
  adCampaignAndSetIds?: string[],
  adObjectiveIds?: string[],
  _memberIds?: string[], // TODO: Implement when backend provides member-campaign associations
  _compareWithPartnershipAds?: boolean // TODO: Implement when backend supports it
): GetInsightsByClientIdQuery => {
  // Use fragment to get properly typed data
  const fragmentData = data as any as SocialAdsAnalytics_QueryFragment;

  if (!fragmentData?.client?.metaAdAccounts) {
    return data;
  }

  // Filter metaAdAccounts based on the provided filters
  const filteredAccounts = fragmentData.client.metaAdAccounts.filter((account) =>
    shouldIncludeAdAccount(account, adAccountIds, adCampaignAndSetIds, adObjectiveIds)
  );

  // TODO: Implement member filtering when backend provides member-campaign associations
  // TODO: Implement compareWithPartnershipAds filtering when backend supports it

  // Note: The insights data (currentInterval, previousInterval, etc.) cannot be filtered
  // client-side because it's aggregated data from the GraphQL query. The filtering here
  // only affects the metaAdAccounts list used for UI display and form population.
  // When backend filtering is implemented, the insights will be properly filtered server-side.

  // Return the data with filtered accounts
  const result = {
    ...data,
    // We need to reconstruct this with the fragment reference structure
  } as GetInsightsByClientIdQuery;

  // Add the fragment refs to maintain the structure
  (result as any)[' $fragmentRefs'] = {
    'SocialAdsAnalytics_QueryFragment': {
      ...fragmentData,
      client: fragmentData.client ? {
        ...fragmentData.client,
        metaAdAccounts: filteredAccounts,
      } : null,
    }
  };

  return result;
};

export const useAdsQueryData = ({
  clientId,
  currentIntervalRange,
  previousIntervalRange,
  startDate,
  endDate,
  fromOldData,
  adAccountIds,
  adCampaignAndSetIds,
  adObjectiveIds,
  memberIds: _memberIds,
  compareWithPartnershipAds: _compareWithPartnershipAds,
}: UseAdsQueryDataProps) => {
  // Track previous values to detect changes
  const prevValues = useRef({
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignAndSetIds,
    adObjectiveIds,
    memberIds: _memberIds,
    compareWithPartnershipAds: _compareWithPartnershipAds,
  });

  const [refetching, setRefetching] = useState(false);

  // Execute the GraphQL query
  const [result, executeQuery] = useQuery({
    query: insightsByClientId,
    variables: {
      clientId: clientId || '',
      currentIntervalRange: {
        startDate: currentIntervalRange.startDate,
        endDate: currentIntervalRange.endDate,
        precision: currentIntervalRange.precision,
      },
      previousIntervalRange: {
        startDate: previousIntervalRange.startDate,
        endDate: previousIntervalRange.endDate,
      },
      fromOldData,
    },
    pause: !clientId,
  });

  const { data, fetching, error } = result;

  // Apply client-side filtering to the data
  const filteredData = useMemo(() => {
    if (!data) return data;

    const hasFilters =
      (adAccountIds && adAccountIds.length > 0) ||
      (adCampaignAndSetIds && adCampaignAndSetIds.length > 0) ||
      (adObjectiveIds && adObjectiveIds.length > 0);

    if (!hasFilters) {
      // No client-side filters applied, return original data
      return data;
    }

    const filtered = applyClientSideFiltering(
      data,
      adAccountIds,
      adCampaignAndSetIds,
      adObjectiveIds,
      _memberIds,
      _compareWithPartnershipAds
    );

    return filtered;
  }, [data, adAccountIds, adCampaignAndSetIds, adObjectiveIds, _memberIds, _compareWithPartnershipAds]);

  // Use fragment to access the data
  const fragmentData = useFragment(socialAdsAnalyticsFragment, filteredData);

  // Check if parameters have changed
  const hasParamsChanged = useMemo(() => {
    const current = {
      clientId,
      currentIntervalRange: JSON.stringify(currentIntervalRange),
      previousIntervalRange: JSON.stringify(previousIntervalRange),
      startDate,
      endDate,
      fromOldData,
      adAccountIds: JSON.stringify(adAccountIds?.sort()),
      adCampaignAndSetIds: JSON.stringify(adCampaignAndSetIds?.sort()),
      adObjectiveIds: JSON.stringify(adObjectiveIds?.sort()),
      memberIds: JSON.stringify(_memberIds?.sort()),
      compareWithPartnershipAds: _compareWithPartnershipAds,
    };

    const previous = {
      clientId: prevValues.current.clientId,
      currentIntervalRange: JSON.stringify(prevValues.current.currentIntervalRange),
      previousIntervalRange: JSON.stringify(prevValues.current.previousIntervalRange),
      startDate: prevValues.current.startDate,
      endDate: prevValues.current.endDate,
      fromOldData: prevValues.current.fromOldData,
      adAccountIds: JSON.stringify(prevValues.current.adAccountIds?.sort()),
      adCampaignAndSetIds: JSON.stringify(prevValues.current.adCampaignAndSetIds?.sort()),
      adObjectiveIds: JSON.stringify(prevValues.current.adObjectiveIds?.sort()),
      memberIds: JSON.stringify(prevValues.current.memberIds?.sort()),
      compareWithPartnershipAds: prevValues.current.compareWithPartnershipAds,
    };

    return JSON.stringify(current) !== JSON.stringify(previous);
  }, [
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignAndSetIds,
    adObjectiveIds,
    _memberIds,
    _compareWithPartnershipAds,
  ]);

  // Update previous values when parameters change
  useEffect(() => {
    if (hasParamsChanged) {
      prevValues.current = {
        clientId,
        currentIntervalRange,
        previousIntervalRange,
        startDate,
        endDate,
        fromOldData,
        adAccountIds,
        adCampaignAndSetIds,
        adObjectiveIds,
        memberIds: _memberIds,
        compareWithPartnershipAds: _compareWithPartnershipAds,
      };
    }
  }, [
    hasParamsChanged,
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignAndSetIds,
    adObjectiveIds,
    _memberIds,
    _compareWithPartnershipAds,
  ]);

  // Manual refetch function with loading state
  const handleRefetch = useCallback(async () => {
    setRefetching(true);
    try {
      await executeQuery({ requestPolicy: 'network-only' });
    } finally {
      setRefetching(false);
    }
  }, [executeQuery]);

  // Process the fragment data for easier consumption
  const processedData = useMemo(() => {
    const client = fragmentData?.client;

    return {
      metaAdAccounts: client?.metaAdAccounts || [],
      currentInterval: client?.currentInterval || null,
      currentIntervalsIntervalInsights: client?.currentIntervalsIntervalInsights || [],
      currentIntervalsIntervalInsightsNotAspireSourced: client?.currentIntervalsIntervalInsightsNotAspireSourced || [],
      previousInterval: client?.previousInterval || null,
      currentIntervalNotAspireSourced: client?.currentIntervalNotAspireSourced || null,
      previousIntervalNotAspireSourced: client?.previousIntervalNotAspireSourced || null,
    };
  }, [fragmentData]);

  return {
    fetching,
    refetching,
    error,
    processedData,
    handleRefetch,
  };
};
